import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Calendar } from 'lucide-react';
import { AuditLog } from '@/types/auditLog';
import { AuditLogCard } from './AuditLogCard';

interface AuditLogListProps {
  logs: AuditLog[];
  isLoading: boolean;
  onLogClick?: (log: AuditLog) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
}

// Group logs by date
const groupLogsByDate = (logs: AuditLog[]): Map<string, AuditLog[]> => {
  const groups = new Map<string, AuditLog[]>();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  logs.forEach(log => {
    const logDate = new Date(log.createdAt);
    logDate.setHours(0, 0, 0, 0);

    let dateKey: string;
    if (logDate.getTime() === today.getTime()) {
      dateKey = 'Today';
    } else if (logDate.getTime() === yesterday.getTime()) {
      dateKey = 'Yesterday';
    } else {
      dateKey = logDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: logDate.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
      });
    }

    if (!groups.has(dateKey)) {
      groups.set(dateKey, []);
    }
    groups.get(dateKey)!.push(log);
  });

  return groups;
};

// Loading skeleton
const LoadingSkeleton: React.FC = () => (
  <div className="space-y-4">
    {[...Array(5)].map((_, i) => (
      <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 bg-gray-200 rounded-lg" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-200 rounded w-3/4" />
            <div className="h-3 bg-gray-200 rounded w-1/2" />
            <div className="h-3 bg-gray-200 rounded w-full" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

// Empty state
const EmptyState: React.FC = () => (
  <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
    <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
    <h3 className="text-lg font-medium text-gray-900 mb-2">No audit logs found</h3>
    <p className="text-sm text-gray-500">
      Try adjusting your filters or date range to see more results.
    </p>
  </div>
);

export const AuditLogList: React.FC<AuditLogListProps> = ({
  logs,
  isLoading,
  onLogClick,
  onLoadMore,
  hasMore,
}) => {
  const groupedLogs = useMemo(() => groupLogsByDate(logs), [logs]);

  if (isLoading && logs.length === 0) {
    return <LoadingSkeleton />;
  }

  if (!isLoading && logs.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-6">
      {Array.from(groupedLogs.entries()).map(([date, dateLogs], groupIndex) => (
        <motion.div
          key={date}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: groupIndex * 0.1 }}
        >
          {/* Date Header */}
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Calendar className="w-4 h-4 text-gray-400" />
              {date}
            </div>
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400">{dateLogs.length} activities</span>
          </div>

          {/* Log Cards */}
          <div className="space-y-3 ml-6 border-l-2 border-gray-100 pl-4">
            {dateLogs.map((log, index) => (
              <motion.div
                key={log.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <AuditLogCard log={log} onClick={onLogClick} />
              </motion.div>
            ))}
          </div>
        </motion.div>
      ))}

      {/* Load More Button */}
      {hasMore && onLoadMore && (
        <div className="text-center pt-4">
          <button
            onClick={onLoadMore}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}

      {/* Loading indicator for infinite scroll */}
      {isLoading && logs.length > 0 && (
        <div className="text-center py-4">
          <div className="inline-flex items-center gap-2 text-sm text-gray-500">
            <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
            Loading more...
          </div>
        </div>
      )}
    </div>
  );
};
